/**
 * Client-side table filtering (CC_05 Block 5). Up to two chained conditions per
 * table, combined with AND or OR. Pure and unit-tested; the FilterBar component
 * drives it and the saved-views API persists the config. The server only stores
 * the config — evaluation happens here.
 */

export type FilterOp =
  | "contains"
  | "eq"
  | "neq"
  | "gt"
  | "lt"
  | "empty"
  | "notEmpty";

export interface FilterCondition {
  field: string;
  op: FilterOp;
  value?: string;
}

export interface FilterConfig {
  combinator: "and" | "or";
  conditions: FilterCondition[];
}

export const EMPTY_FILTER: FilterConfig = { combinator: "and", conditions: [] };

const isEmpty = (v: unknown) => v === null || v === undefined || v === "";

/** Evaluate one condition against a single cell value. */
export function matchCondition(cell: unknown, cond: FilterCondition): boolean {
  switch (cond.op) {
    case "empty":
      return isEmpty(cell);
    case "notEmpty":
      return !isEmpty(cell);
    case "gt":
    case "lt": {
      let a = Number(cell);
      let b = Number(cond.value);
      // Fällt die numerische Deutung aus (z. B. Datums-Strings "YYYY-MM-DD"),
      // versuche eine Datums-Deutung, damit Bereichsfilter auf date-Spalten
      // nicht stumm leere Treffermengen liefern.
      if (Number.isNaN(a) || Number.isNaN(b)) {
        const da = Date.parse(String(cell ?? ""));
        const db = Date.parse(String(cond.value ?? ""));
        if (Number.isNaN(da) || Number.isNaN(db)) {
          return false;
        }
        a = da;
        b = db;
      }
      return cond.op === "gt" ? a > b : a < b;
    }
    case "eq":
      return (
        String(cell ?? "").toLowerCase() ===
        String(cond.value ?? "").toLowerCase()
      );
    case "neq":
      return (
        String(cell ?? "").toLowerCase() !==
        String(cond.value ?? "").toLowerCase()
      );
    case "contains":
    default:
      return String(cell ?? "")
        .toLowerCase()
        .includes(String(cond.value ?? "").toLowerCase());
  }
}

/** True if a row satisfies the whole filter (empty filter → always true). */
export function matchesFilter<T>(
  row: T,
  config: FilterConfig,
  accessor: (row: T, field: string) => unknown,
): boolean {
  const active = (config.conditions || []).filter(
    (c) => c.field && (c.op === "empty" || c.op === "notEmpty" || c.value),
  );
  if (active.length === 0) {
    return true;
  }
  const results = active.map((c) => matchCondition(accessor(row, c.field), c));
  return config.combinator === "or"
    ? results.some(Boolean)
    : results.every(Boolean);
}

/** Return the rows that satisfy the filter (a new array). */
export function applyFilter<T>(
  rows: readonly T[],
  config: FilterConfig,
  accessor: (row: T, field: string) => unknown,
): T[] {
  return rows.filter((r) => matchesFilter(r, config, accessor));
}
